import { Badge, Form } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useUpdateSprawaMyStatusMutation, useListSprawaStatusDefinitionsQuery } from '@/store/sprawyApi';
import type { SprawaDto, SprawaStatusDefinitionDto } from '@/types';
import {
  BoardContainer, Column, ColumnHeader, ColumnBody,
  Card, CardSignature, CardCourt, CardMeta, CardStatusBadge, StatusSelect, EmptyColumn,
} from './KanbanBoard.styled';

interface Props {
  sprawy: SprawaDto[];
  statusDefinitions: SprawaStatusDefinitionDto[];
  onCardClick: (sprawaId: string) => void;
}

export default function KanbanBoard({ sprawy, statusDefinitions, onCardClick }: Props) {
  const { t } = useTranslation();
  const [updateStatus] = useUpdateSprawaMyStatusMutation();
  const { data: allDefs } = useListSprawaStatusDefinitionsQuery();

  const grouped = new Map<string | null, SprawaDto[]>();

  // Initialize columns
  for (const def of statusDefinitions) {
    grouped.set(def.id, []);
  }
  grouped.set(null, []); // unassigned column

  // Group sprawy
  for (const s of sprawy) {
    const key = s.statusDefinitionId;
    const list = grouped.get(key) ?? grouped.get(null)!;
    list.push(s);
  }

  const handleStatusChange = async (sprawaId: string, statusDefinitionId: string) => {
    if (statusDefinitionId) {
      await updateStatus({ sprawaId, statusDefinitionId });
    }
  };

  const renderColumn = (def: SprawaStatusDefinitionDto | null, items: SprawaDto[]) => {
    const color = def?.color ?? '#adb5bd';
    const name = def?.name ?? t('sprawy.board.unassigned');

    return (
      <Column key={def?.id ?? 'unassigned'}>
        <ColumnHeader $color={color}>
          <span>{name}</span>
          <Badge bg="light" text="dark" pill>{items.length}</Badge>
        </ColumnHeader>
        <ColumnBody>
          {items.length === 0 ? (
            <EmptyColumn>{t('sprawy.board.emptyColumn')}</EmptyColumn>
          ) : (
            items.map((s) => (
              <Card key={s.id} $borderColor={color} onClick={() => onCardClick(s.id)}>
                <CardSignature>{s.signature}</CardSignature>
                {s.court && <CardCourt>{s.court}</CardCourt>}
                <CardMeta>
                  <CardStatusBadge>
                    <Badge bg="secondary">{t(`sprawaStatus.${s.portalStatus}`)}</Badge>
                  </CardStatusBadge>
                  <span>{t('common.documentsShort', { count: s.documentCount })}</span>
                </CardMeta>
                <StatusSelect>
                  <Form.Select
                    size="sm"
                    value={s.statusDefinitionId ?? ''}
                    onClick={(e) => e.stopPropagation()}
                    onChange={(e) => { e.stopPropagation(); handleStatusChange(s.id, e.target.value); }}
                  >
                    <option value="" disabled>—</option>
                    {(allDefs ?? statusDefinitions).map((sd) => (
                      <option key={sd.id} value={sd.id}>{sd.name}</option>
                    ))}
                  </Form.Select>
                </StatusSelect>
              </Card>
            ))
          )}
        </ColumnBody>
      </Column>
    );
  };

  return (
    <BoardContainer>
      {statusDefinitions.map((def) => renderColumn(def, grouped.get(def.id) ?? []))}
      {(grouped.get(null)?.length ?? 0) > 0 && renderColumn(null, grouped.get(null)!)}
    </BoardContainer>
  );
}
